import React, { useState, useCallback, useEffect, useRef } from "react";
import useFetch from "../../../hooks/useFetch";
import QuickSpinHeader from "./components/QuickSpinHeader";
import Wheel from "./components/Wheel";
import QuickSpinPanel from "./components/QuickSpinPanel";
import { createQuickSpinService } from "./services/quickSpinService";
import {
  generateId,
  calculateTargetRotation,
  calculateWinnerIndex,
  shuffleEntries,
  sortEntries,
  getRandomEntry,
} from "./utils/wheelUtils";

const DEFAULT_SETTINGS = {
  allowDuplicates: false,
  removeAfterSpin: false,
  preventRepeat: false,
  soundEnabled: false,
  confettiEnabled: false,
  rankingMode: "fresh", // "fresh" | "leaderboard"
};

const CONFETTI_COLORS = ["#EF4444", "#F59E0B", "#10B981", "#3B82F6", "#8B5CF6", "#EC4899"];
const MAX_NAME_LENGTH = 24;

const createDefaultEntries = () => [];

// ─── Win-moment effects (no external deps) ─────────────────
const fireConfetti = () => {
  const container = document.createElement("div");
  container.style.cssText =
    "position:fixed;inset:0;pointer-events:none;z-index:60;overflow:hidden;";
  document.body.appendChild(container);

  for (let i = 0; i < 60; i++) {
    const piece = document.createElement("div");
    const size = 6 + Math.random() * 6;
    piece.style.cssText = `
      position:absolute; top:-10px; left:${Math.random() * 100}%;
      width:${size}px; height:${size * 0.4}px;
      background:${CONFETTI_COLORS[i % CONFETTI_COLORS.length]};
      transform:rotate(${Math.random() * 360}deg);
      animation: quickspin-confetti-fall ${1.6 + Math.random() * 1.2}s ease-in forwards;
      animation-delay:${Math.random() * 0.3}s;
    `;
    container.appendChild(piece);
  }
  setTimeout(() => container.remove(), 3200);
};

const playWinSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [523.25, 659.25, 783.99].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = "sine";
      const start = ctx.currentTime + i * 0.1;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.25, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.35);
      osc.start(start);
      osc.stop(start + 0.4);
    });
  } catch (_) {
    // Audio not available — fail silently
  }
};

const QuickSpinPage = () => {
  const api = useFetch();
  // Patch B: memoize service instead of recreating every render
  const service = useRef(createQuickSpinService(api)).current;

  const [wheel, setWheel] = useState({
    id: null,
    name: "QuickSpin",
    entries: createDefaultEntries(),
    results: [],
    lastResult: null,
    settings: { ...DEFAULT_SETTINGS },
    leaderboard: {}, // { [entryId]: { name, wins } } — persists across spins
    lastSpinRanking: [], // [{ id, text }] — winner first, rest shuffled, from the most recent spin
  });

  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [activeTab, setActiveTab] = useState("entries");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showList, setShowList] = useState(false);
  const [savedWheels, setSavedWheels] = useState([]);
  const [usage, setUsage] = useState(null);
  const [showUsage, setShowUsage] = useState(false);
  const [toast, setToast] = useState(null);
  const [listError, setListError] = useState(false);
  const [showRankings, setShowRankings] = useState(false);

  // Patch D: responsive wheel size
  const [wheelSize, setWheelSize] = useState(320);

  const spinTimeoutRef = useRef(null);
  const hasUnsavedChanges = useRef(false);

  // Local draft fallback
  useEffect(() => {
    try {
      const draft = localStorage.getItem("quickspin-draft");
      if (draft) {
        const parsed = JSON.parse(draft);
        if (parsed?.entries) setWheel((w) => ({ ...w, ...parsed }));
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (wheel.entries.length || wheel.results.length) {
      localStorage.setItem(
        "quickspin-draft",
        JSON.stringify({
          id: wheel.id,
          name: wheel.name,
          entries: wheel.entries,
          results: wheel.results,
          lastResult: wheel.lastResult,
          settings: wheel.settings,
          leaderboard: wheel.leaderboard,
          lastSpinRanking: wheel.lastSpinRanking,
        }),
      );
      hasUnsavedChanges.current = true;
    }
  }, [wheel]);

  // Patch D: track viewport size for the wheel
  useEffect(() => {
    const updateSize = () => setWheelSize(Math.min(360, window.innerWidth - 48));
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  const showToast = (msg, type = "info") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  };

  // ─── Spin ───────────────────────────────────────────────
  const handleSpin = useCallback(() => {
    if (isSpinning || wheel.entries.length < 2) return;

    setIsSpinning(true);

    const lastWinnerId =
      wheel.results.length > 0 ? wheel.results[0].entryId : null;

    const targetEntry = getRandomEntry(
      wheel.entries,
      lastWinnerId,
      wheel.settings.preventRepeat,
    );
    if (!targetEntry) {
      setIsSpinning(false);
      return;
    }

    const targetIndex = wheel.entries.findIndex((e) => e.id === targetEntry.id);
    const targetRotation = calculateTargetRotation(
      rotation,
      targetIndex,
      wheel.entries.length,
      5 + Math.floor(Math.random() * 3),
    );

    setRotation(targetRotation);

    spinTimeoutRef.current = setTimeout(() => {
      const finalIndex = calculateWinnerIndex(
        targetRotation,
        wheel.entries.length,
      );
      const winner = wheel.entries[finalIndex] || targetEntry;

      const newResult = {
        entryId: winner.id,
        entryName: winner.text,
        timestamp: new Date().toISOString(),
        spinNumber: wheel.results.length + 1,
      };

      setWheel((prev) => {
        const participants = prev.entries; // full pool for this spin, before any removal
        let nextEntries = participants;
        if (prev.settings.removeAfterSpin) {
          nextEntries = participants.filter((e) => e.id !== winner.id);
        }

        // Bonus: Rankings — winner first, everyone else shuffled. Doesn't
        // touch wheel/rotation logic at all, purely a display extra.
        const others = participants.filter((e) => e.id !== winner.id);
        const lastSpinRanking = [
          { id: winner.id, text: winner.text },
          ...shuffleEntries(others).map((e) => ({ id: e.id, text: e.text })),
        ];

        const nextLeaderboard = { ...prev.leaderboard };
        const existing = nextLeaderboard[winner.id];
        nextLeaderboard[winner.id] = {
          name: winner.text,
          wins: (existing?.wins || 0) + 1,
        };

        return {
          ...prev,
          entries: nextEntries,
          results: [newResult, ...prev.results],
          lastResult: winner.text,
          lastSpinRanking,
          leaderboard: nextLeaderboard,
        };
      });

      // Patch C + E: win-moment feedback
      if (navigator.vibrate) navigator.vibrate([40, 30, 80]);
      if (wheel.settings.confettiEnabled) fireConfetti();
      if (wheel.settings.soundEnabled) playWinSound();

      setIsSpinning(false);
      setActiveTab("results");
    }, 5600);
  }, [isSpinning, wheel, rotation]);

  // ─── Entry management ───────────────────────────────────
  const addEntry = (names) => {
    // names can be a string (legacy) or an array of strings
    const list = Array.isArray(names) ? names : [names];

    setWheel((prev) => {
      const newEntries = list
        .map((text) => text.trim())
        .filter((text) => text.length > 0)
        .map((text) => ({
          id: generateId(),
          text: text.length > MAX_NAME_LENGTH ? text.slice(0, MAX_NAME_LENGTH) : text,
          imageUrl: null,
        }));

      // Patch A: append instead of overwrite, respect allowDuplicates
      const combined = prev.settings.allowDuplicates
        ? [...prev.entries, ...newEntries]
        : [
            ...prev.entries,
            ...newEntries.filter(
              (n) =>
                !prev.entries.some(
                  (e) => e.text.toLowerCase() === n.text.toLowerCase(),
                ),
            ),
          ];

      if (combined.length > 25) {
        showToast("Maximum 25 names allowed", "error");
      }

      return { ...prev, entries: combined.slice(0, 25) };
    });
  };

  const editEntry = (id, text) => {
    setWheel((prev) => ({
      ...prev,
      entries: prev.entries.map((e) => (e.id === id ? { ...e, text } : e)),
    }));
  };

  const deleteEntry = (id) => {
    setWheel((prev) => ({
      ...prev,
      entries: prev.entries.filter((e) => e.id !== id),
    }));
  };

  const handleShuffle = () => {
    setWheel((prev) => ({ ...prev, entries: shuffleEntries(prev.entries) }));
  };

  const handleSort = (dir = "asc") => {
    setWheel((prev) => ({ ...prev, entries: sortEntries(prev.entries, dir) }));
  };

  const clearResults = () => {
    setWheel((prev) => ({ ...prev, results: [], lastResult: null }));
  };

  const clearEntries = () => {
    setWheel((prev) => ({ ...prev, entries: [] }));
  };

  const updateSettings = (partial) => {
    setWheel((prev) => ({
      ...prev,
      settings: { ...prev.settings, ...partial },
    }));
  };

  const updateName = (name) => {
    setWheel((prev) => ({ ...prev, name: name.slice(0, 40) }));
  };

  // ─── Save / Load / List ─────────────────────────────────
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = {
        name: wheel.name,
        entries: wheel.entries,
        settings: wheel.settings,
        results: wheel.results,
        lastResult: wheel.lastResult,
        leaderboard: wheel.leaderboard,
        lastSpinRanking: wheel.lastSpinRanking,
      };

      let res;
      if (wheel.id) {
        res = await service.updateWheel(wheel.id, payload);
      } else {
        res = await service.createWheel(payload);
      }

      if (res?.success && res.wheel) {
        setWheel((prev) => ({ ...prev, id: res.wheel._id || res.wheel.id }));
        hasUnsavedChanges.current = false;
        showToast("Saved ✓", "success");
      } else {
        showToast("Unable to save wheel", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Unable to save wheel. Please try again.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleList = async () => {
    setIsLoading(true);
    setListError(false);
    setShowList(true);
    try {
      const res = await service.getWheels({ limit: 30 });
      if (res?.success) {
        setSavedWheels(res.wheels || []);
      } else {
        setListError(true);
        showToast("Unable to load saved wheels", "error");
      }
    } catch (err) {
      setListError(true);
      showToast("Unable to load saved wheels", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const loadWheel = async (id) => {
    setIsLoading(true);
    try {
      const res = await service.getWheel(id);
      if (res?.success && res.wheel) {
        const w = res.wheel;
        setWheel({
          id: w._id || w.id,
          name: w.name || "QuickSpin",
          entries: w.entries || [],
          results: w.results || [],
          lastResult: w.lastResult || null,
          settings: { ...DEFAULT_SETTINGS, ...(w.settings || {}) },
          leaderboard: w.leaderboard || {},
          lastSpinRanking: w.lastSpinRanking || [],
        });
        setRotation(0);
        setShowList(false);
        hasUnsavedChanges.current = false;
        showToast("Wheel loaded", "success");
      }
    } catch (err) {
      showToast("Unable to load wheel", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleNew = () => {
    if (hasUnsavedChanges.current) {
      const ok = window.confirm(
        "Start a new wheel? Unsaved changes will be lost.",
      );
      if (!ok) return;
    }
    setWheel({
      id: null,
      name: "QuickSpin",
      entries: createDefaultEntries(),
      results: [],
      lastResult: null,
      settings: { ...DEFAULT_SETTINGS },
      leaderboard: {},
      lastSpinRanking: [],
    });
    setRotation(0);
    setActiveTab("entries");
    localStorage.removeItem("quickspin-draft");
    hasUnsavedChanges.current = false;
  };

  const handleShare = () => {
    const url = wheel.id
      ? `${window.location.origin}/iqgames/quickspin?id=${wheel.id}`
      : window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      showToast("Link copied!", "success");
    });
  };

  const handleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const handleUsage = async () => {
    setShowUsage(true);
    try {
      const res = await service.getUsage();
      if (res?.success) setUsage(res.usage);
      else {
        // local fallback
        setUsage({
          spins: wheel.results.length,
          wheels: 1,
          savedWheels: wheel.id ? 1 : 0,
        });
      }
    } catch {
      setUsage({
        spins: wheel.results.length,
        wheels: 1,
        savedWheels: wheel.id ? 1 : 0,
      });
    }
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (spinTimeoutRef.current) clearTimeout(spinTimeoutRef.current);
    };
  }, []);

  const allEntriesGone =
    wheel.settings.removeAfterSpin && wheel.entries.length === 0;

  return (
    <div className="min-h-screen bg-[#0a0e17] text-white flex flex-col">
      <style>{`
        @keyframes quickspin-confetti-fall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(360deg); opacity: 0; }
        }
      `}</style>

      <QuickSpinHeader
        onUsage={handleUsage}
        onNew={handleNew}
        onList={handleList}
        onSave={handleSave}
        onShare={handleShare}
        onFullscreen={handleFullscreen}
        isSaving={isSaving}
        isSpinning={isSpinning}
      />

      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-16 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-lg text-sm font-medium shadow-lg ${
            toast.type === "success"
              ? "bg-emerald-600"
              : toast.type === "error"
                ? "bg-red-600"
                : "bg-slate-700"
          }`}
        >
          {toast.msg}
        </div>
      )}

      <main className="flex-1 flex flex-col lg:flex-row gap-4 p-3 sm:p-4 max-w-7xl mx-auto w-full">
        {/* Wheel column */}
        <div className="flex flex-col items-center justify-center flex-1 min-h-[340px] relative">
          {allEntriesGone ? (
            <div className="text-center space-y-4">
              <p className="text-xl font-semibold text-amber-400">
                🎉 All entries have been selected.
              </p>
              <button
                type="button"
                onClick={handleNew}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-500 rounded-xl font-medium"
              >
                Start New Wheel
              </button>
            </div>
          ) : (
            <>
              {wheel.name && (
                <h2 className="text-sm sm:text-base font-medium text-slate-300 mb-2 text-center truncate max-w-[90%]">
                  {wheel.name}
                </h2>
              )}
              <Wheel
                entries={wheel.entries}
                rotation={rotation}
                isSpinning={isSpinning}
                size={wheelSize}
              />

              {/* Result banner */}
              {wheel.lastResult && !isSpinning && (
                <div
                  className="mt-4 text-center animate-pulse"
                  role="status"
                  aria-live="polite"
                >
                  <p className="text-xs uppercase tracking-widest text-amber-400/80">
                    Winner!
                  </p>
                  <p className="text-2xl sm:text-3xl font-bold text-amber-300">
                    {wheel.lastResult}
                  </p>
                </div>
              )}

              {wheel.results.length > 0 && !isSpinning && (
                <button
                  type="button"
                  onClick={() => setShowRankings(true)}
                  className="mt-3 px-4 py-1.5 rounded-full bg-slate-800/80 border border-slate-700 text-xs font-medium text-amber-300 hover:bg-slate-700 transition"
                >
                  🏆 Rankings
                </button>
              )}

              <button
                type="button"
                onClick={handleSpin}
                disabled={isSpinning || wheel.entries.length < 2}
                className="mt-5 px-10 py-3.5 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 disabled:opacity-40 text-lg font-bold shadow-lg shadow-blue-900/40 transition active:scale-95 min-h-[48px]"
                aria-label="Spin wheel"
              >
                {isSpinning ? "Spinning…" : "SPIN"}
              </button>
            </>
          )}
        </div>

        {/* Panel column */}
        <div className="w-full lg:w-[340px] xl:w-[380px] flex-shrink-0">
          <QuickSpinPanel
            name={wheel.name}
            onRename={updateName}
            onNotify={showToast}
            entries={wheel.entries}
            results={wheel.results}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            settings={wheel.settings}
            onSettingsChange={updateSettings}
            onAddEntry={addEntry}
            onEditEntry={editEntry}
            onDeleteEntry={deleteEntry}
            onClearEntries={clearEntries}
            onShuffle={handleShuffle}
            onSort={handleSort}
            onClearResults={clearResults}
            isSpinning={isSpinning}
            showAdvanced={showAdvanced}
            setShowAdvanced={setShowAdvanced}
          />
        </div>
      </main>

      {/* List modal */}
      {showList && (
        <div className="fixed inset-0 z-40 bg-black/70 flex items-end sm:items-center justify-center p-4">
          <div className="bg-slate-900 rounded-2xl border border-slate-700 w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
              <h2 className="font-semibold text-lg text-cyan-200">My QuickSpins</h2>
              <button
                type="button"
                onClick={() => setShowList(false)}
                className="w-fit p-2 text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-3 space-y-2">
              {isLoading && (
                <p className="text-center text-slate-400 py-8">Loading…</p>
              )}
              {!isLoading && listError && (
                <div className="text-center py-8 space-y-2">
                  <p className="text-red-400 text-sm">Couldn't load your wheels.</p>
                  <button
                    type="button"
                    onClick={handleList}
                    className="text-xs text-blue-400 hover:underline"
                  >
                    Try again
                  </button>
                </div>
              )}
              {!isLoading && !listError && savedWheels.length === 0 && (
                <p className="text-center text-slate-500 py-8">
                  No saved wheels yet
                </p>
              )}
              {savedWheels.map((w) => (
                <button
                  key={w._id || w.id}
                  type="button"
                  onClick={() => loadWheel(w._id || w.id)}
                  className="w-full text-left px-4 py-3 rounded-xl bg-slate-800/80 hover:bg-slate-800 border border-slate-700/50 transition"
                >
                  <p className="font-medium">{w.name}</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {(w.entries || []).length} Entries ·{" "}
                    {(w.results || []).length} Spins
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Bonus Rankings modal */}
      {showRankings && (
        <div className="fixed inset-0 z-40 bg-black/70 flex items-end sm:items-center justify-center p-4">
          <div className="bg-slate-900 rounded-2xl border border-slate-700 w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
              <h2 className="font-semibold text-lg text-cyan-200">🏆 Bonus: Rankings</h2>
              <button
                type="button"
                onClick={() => setShowRankings(false)}
                className="w-fit p-2 text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            <p className="px-4 pt-3 text-xs text-slate-500">
              {wheel.settings.rankingMode === "leaderboard"
                ? "Cumulative wins across all spins on this wheel."
                : "This spin's winner, followed by everyone else in random order."}
            </p>
            <div className="overflow-y-auto flex-1 p-3 space-y-1.5">
              {wheel.settings.rankingMode === "leaderboard" ? (
                Object.values(wheel.leaderboard)
                  .sort((a, b) => b.wins - a.wins)
                  .map((row, idx) => (
                    <div
                      key={row.name + idx}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${
                        idx === 0
                          ? "bg-amber-500/10 border border-amber-500/40"
                          : "bg-slate-800/50"
                      }`}
                    >
                      <span
                        className={`w-6 text-center font-semibold ${
                          idx === 0 ? "text-amber-300" : "text-slate-500"
                        }`}
                      >
                        {idx + 1}
                      </span>
                      <span className="flex-1 text-slate-100">{row.name}</span>
                      <span className="text-xs text-slate-400">
                        {row.wins} win{row.wins === 1 ? "" : "s"}
                      </span>
                    </div>
                  ))
              ) : wheel.lastSpinRanking.length === 0 ? (
                <p className="text-center text-slate-500 text-sm py-8">
                  Spin the wheel to see rankings
                </p>
              ) : (
                wheel.lastSpinRanking.map((row, idx) => (
                  <div
                    key={row.id}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${
                      idx === 0
                        ? "bg-amber-500/10 border border-amber-500/40"
                        : "bg-slate-800/50"
                    }`}
                  >
                    <span
                      className={`w-6 text-center font-semibold ${
                        idx === 0 ? "text-amber-300" : "text-slate-500"
                      }`}
                    >
                      {idx + 1}
                    </span>
                    <span className="flex-1 text-slate-100">{row.text}</span>
                    {idx === 0 && <span className="text-amber-300 text-xs">Winner</span>}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Usage modal */}
      {showUsage && (
        <div className="fixed inset-0 z-40 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-slate-900 rounded-2xl border border-slate-700 w-full max-w-xs p-5">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-semibold text-cyan-200">Usage</h2>
              <button
                type="button"
                onClick={() => setShowUsage(false)}
                className="w-fit p-2 text-slate-400"
              >
                ✕
              </button>
            </div>
            {usage ? (
              <ul className="space-y-2 text-sm">
                <li className="flex justify-between">
                  <span className="text-slate-400">Spins</span>
                  <span>{usage.spins}</span>
                </li>
                <li className="flex justify-between">
                  <span className="text-slate-400">Wheels</span>
                  <span>{usage.wheels}</span>
                </li>
                <li className="flex justify-between">
                  <span className="text-slate-400">Saved</span>
                  <span>{usage.savedWheels}</span>
                </li>
              </ul>
            ) : (
              <p className="text-slate-400 text-sm">Loading…</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickSpinPage;